import { z } from 'zod'

// The DB column supports a superset (kept for schema stability); the
// public form only ever offers these three — see docs/DECISIONS.md.
export const requestTypeEnum = z.enum(['new_request', 'bug', 'update', 'enhancement', 'feature'])
export const publicRequestTypeEnum = z.enum(['bug', 'enhancement', 'feature'])
export const requestStatusEnum = z.enum(['new', 'triaged', 'accepted', 'rejected', 'needs_info'])
export const positionEnum = z.enum([
  'sales_agent',
  'telesales_agent',
  'sales_team_leader',
  'telesales_team_leader',
  'sales_manager',
  'telesales_manager',
  'hr',
  'hr_manager',
])
export type RequestType = z.infer<typeof requestTypeEnum>
export type PublicRequestType = z.infer<typeof publicRequestTypeEnum>
export type RequestStatus = z.infer<typeof requestStatusEnum>
export type Position = z.infer<typeof positionEnum>

export const MAX_ATTACHMENT_BYTES = 4 * 1024 * 1024 // ~4MB decoded — stored inline in D1, not object storage
export const ALLOWED_IMAGE_MIME = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/bmp',
] as const

/** A single uniform payload for every request type — one free-text box
 * plus an optional screenshot, no adaptive per-type fields. Stored as a
 * data: URL directly in D1 (no object storage set up on this account
 * yet — see docs/DECISIONS.md#screenshot-attachments) rather than a
 * separate uploads table, so there's a hard size cap enforced here. */
export const requestPayloadSchema = z.object({
  description: z.string().trim().min(1).max(4000),
  attachmentDataUrl: z
    .string()
    .refine((v) => ALLOWED_IMAGE_MIME.some((mime) => v.startsWith(`data:${mime};base64,`)), {
      message: 'Attachment must be an image (jpg, png, gif, webp, heic, or bmp).',
    })
    .refine((v) => v.length <= MAX_ATTACHMENT_BYTES * 1.4, { message: 'Attachment is too large.' })
    .optional(),
})
export type RequestPayload = z.infer<typeof requestPayloadSchema>

export const createRequestSchema = z.object({
  requesterName: z.string().trim().min(1).max(120),
  // Required for every request type — there's always someone to follow
  // up with, not just for bug reports (see docs/DECISIONS.md).
  requesterEmail: z
    .string()
    .trim()
    .toLowerCase()
    .email('يرجى إدخال بريد إلكتروني صحيح.')
    .max(200),
  position: positionEnum,
  team: z.string().trim().max(120).optional(), // free text — see the schema comment on requests.team
  departmentId: z.string().min(1), // the "Company" picker — see docs/DECISIONS.md
  type: publicRequestTypeEnum,
  payload: requestPayloadSchema,
  turnstileToken: z.string().optional(),
})
export type CreateRequestInput = z.infer<typeof createRequestSchema>

export const replySchema = z.object({
  body: z.string().trim().min(1).max(4000),
})

export const triageActionSchema = z.object({
  action: z.enum(['accept', 'reject', 'needs_info']),
  rejectionReason: z.string().trim().max(1000).optional(),
  message: z.string().trim().max(4000).optional(),
})

export interface RequestMessageDTO {
  id: string
  author: 'operator' | 'requester' | 'system'
  bodyMd: string
  createdAt: number
}

export interface RequestDTO {
  id: string
  publicId: string
  type: RequestType
  departmentId: string
  requesterName: string
  requesterEmail: string
  position: Position | null
  team: string | null
  desiredDate: number | null
  payload: RequestPayload
  status: RequestStatus
  rejectionReason: string | null
  convertedTaskId: string | null
  createdAt: number
}

export interface DepartmentDTO {
  id: string
  slug: string
  nameEn: string
  nameAr: string
}
